import React, { useMemo, useState } from 'react';
import { Card } from '@ui';
import { TrackingActions } from '@/features/Tracking';
import { useAppSelector } from '@/state';
import { InventoryApi } from '@/state/api';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Input,
  Skeleton,
  Table,
  Text,
} from '@chakra-ui/react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import {
  HiOutlineArchiveBox,
  HiOutlineClipboardDocumentList,
  HiOutlineExclamationTriangle,
  HiOutlineShieldExclamation,
  HiOutlineChartBar,
  HiOutlineCube,
  HiOutlineCog6Tooth,
  HiOutlineArrowTrendingUp,
} from 'react-icons/hi2';

type InventoryDashboardKPI = {
  key: string;
  label: string;
  value: number;
  meta?: Record<string, any>;
};

type InventoryDashboardChartPoint = {
  key: string;
  label: string;
  value: number;
};

type InventoryDashboardReceiptDay = {
  date: string;
  overdue: number;
  due_today: number;
  upcoming: number;
};

type InventoryDashboardShortageRow = {
  part_id: number;
  part_number: string;
  description?: string | null;
  open_short_qty: number;
  open_log_count: number;
  last_shortage_at?: string | null;
};

type InventoryDashboardRecentActivity = {
  type: 'RECEIPT' | 'QC' | 'CONSUMPTION' | 'FABRICATION_OUTPUT';
  title: string;
  subtitle?: string | null;
  quantity?: number;
  created_at: string;
};

type InventoryDashboardInventoryRow = {
  row_key: string;
  source_type: 'PART' | 'MANUAL';
  part_id?: number | null;
  part_number: string;
  description?: string | null;
  available_qty: number;
  on_hold_qty: number;
  net_available_qty: number;
  incoming_open_qty: number;
  overdue_incoming_qty: number;
  pending_qc_qty: number;
  consumed_30d_qty: number;
  fabricated_30d_qty: number;
  open_shortage_qty: number;
  status: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'INCOMING_ONLY';
};

type InventoryDashboardResponse = {
  project_id: number;
  updated_at: string;
  low_stock_threshold: number;
  window_days: number;
  kpis: InventoryDashboardKPI[];
  stock_status_breakdown: InventoryDashboardChartPoint[];
  receipts_by_day: InventoryDashboardReceiptDay[];
  top_shortage_parts: InventoryDashboardShortageRow[];
  recent_activity: InventoryDashboardRecentActivity[];
  inventory_table: InventoryDashboardInventoryRow[];
};

const STATUS_COLORS: Record<string, string> = {
  IN_STOCK: '#22c55e',
  LOW_STOCK: '#f59e0b',
  OUT_OF_STOCK: '#ef4444',
  INCOMING_ONLY: '#3b82f6',
};

const STATUS_STYLES: Record<
  InventoryDashboardInventoryRow['status'],
  { bg: string; color: string; label: string }
> = {
  IN_STOCK: { bg: 'green.50', color: 'green.700', label: 'In stock' },
  LOW_STOCK: { bg: 'yellow.50', color: 'yellow.800', label: 'Low stock' },
  OUT_OF_STOCK: { bg: 'red.50', color: 'red.700', label: 'Out of stock' },
  INCOMING_ONLY: {
    bg: 'blue.50',
    color: 'blue.700',
    label: 'Incoming only',
  },
};

const ACTIVITY_STYLES: Record<
  InventoryDashboardRecentActivity['type'],
  { bg: string; color: string; label: string }
> = {
  RECEIPT: { bg: 'green.50', color: 'green.700', label: 'Receipt' },
  QC: { bg: 'purple.50', color: 'purple.700', label: 'QC' },
  CONSUMPTION: { bg: 'orange.50', color: 'orange.700', label: 'Consumption' },
  FABRICATION_OUTPUT: {
    bg: 'blue.50',
    color: 'blue.700',
    label: 'Fabrication',
  },
};

const PANEL_BORDER = 'rgba(148, 163, 184, 0.22)';
const SUBTLE_BG = 'rgba(148, 163, 184, 0.07)';
const SURFACE_BG = '#11131a';

const TOP_PANEL_HEIGHT = '440px';
const SHORTAGE_PANEL_HEIGHT = '440px';
const DETAIL_PANEL_HEIGHT = '520px';
const ACTIVITY_PANEL_HEIGHT = '520px';

/**
 * Explicit scroll viewport heights.
 * This avoids relying on nested flex/Skeleton height inheritance,
 * which is what usually breaks scrolling in cards like these.
 */
const SHORTAGE_SCROLL_HEIGHT = '330px';
const ACTIVITY_SCROLL_HEIGHT = '410px';
const STOCK_CHART_HEIGHT = '220px';

const InventoryDashboard = () => {
  const selectedProject = useAppSelector((state) => state.project.selectedProject);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'ALL' | InventoryDashboardInventoryRow['status']
  >('ALL');

  const {
    data,
    isLoading,
    isFetching,
    isError,
  } = InventoryApi.Api.useGetInventoryDashboardQuery(
    selectedProject?.id
      ? {
          params: { project_id: selectedProject.id },
          query: {
            low_stock_threshold: 5,
            window_days: 14,
            lookback_days: 30,
          },
        }
      : skipToken,
  );

  const dashboard = (data ?? null) as InventoryDashboardResponse | null;
  const showSkeleton = !dashboard && (isLoading || isFetching);

  const kpiMap = useMemo(() => {
    const map = new Map<string, InventoryDashboardKPI>();
    (dashboard?.kpis ?? []).forEach((item) => map.set(item.key, item));
    return map;
  }, [dashboard]);

  const filteredRows = useMemo(() => {
    const rows = dashboard?.inventory_table ?? [];

    return rows.filter((row) => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        row.part_number.toLowerCase().includes(q) ||
        (row.description ?? '').toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === 'ALL' ? true : row.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [dashboard, search, statusFilter]);

  const stockBreakdown = dashboard?.stock_status_breakdown ?? [];
  const receiptsByDay = dashboard?.receipts_by_day ?? [];
  const topShortageParts = dashboard?.top_shortage_parts ?? [];
  const recentActivity = dashboard?.recent_activity ?? [];

  if (!selectedProject?.id) {
    return (
      <Card>
        <Flex minH="260px" align="center" justify="center">
          <Text color="fg.muted">Select a project first.</Text>
        </Flex>
      </Card>
    );
  }

  if (isError && !dashboard) {
    return (
      <Card>
        <Flex
          minH="260px"
          align="center"
          justify="center"
          flexDirection="column"
          gap={2}
        >
          <Text fontWeight="bold" fontSize="lg">
            Could not load inventory dashboard
          </Text>
          <Text color="fg.muted" textAlign="center">
            Check that the inventory dashboard API is registered in the aggregated
            state API and that the backend route is returning 200.
          </Text>
        </Flex>
      </Card>
    );
  }

  return (
    <Flex flexDirection="column" gapY={4} w="full" minW={0}>
      <Card>
        <Flex
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', xl: 'center' }}
          flexDirection={{ base: 'column', xl: 'row' }}
          gap={4}
        >
          <Box>
            <Text fontWeight="bold" fontSize={{ base: '2xl', xl: '3xl' }}>
              Inventory Dashboard
            </Text>
            <Text color="fg.muted" mt={1}>
              Real-time stock, incoming receipts, QC pressure, shortages, and
              fabrication output
            </Text>
          </Box>

          <HStack gap={3} flexWrap="wrap">
           
            <Badge
              px={3}
              py={1.5}
              borderRadius="full"
              bg="bg.emphasized"
              color="fg"
              borderWidth="1px"
              borderColor={PANEL_BORDER}
            >
              {showSkeleton
                ? 'Refreshing...'
                : `Updated ${dashboard?.updated_at ? formatDateTime(dashboard.updated_at) : '—'}`}
            </Badge>
          </HStack>
        </Flex>
      </Card>

      <Grid templateColumns="repeat(12, minmax(0, 1fr))" gap={4}>
        <GridItem colSpan={{ base: 12, md: 6, xl: 3 }} minW={0}>
          <StatCard
            title={kpiMap.get('total_skus')?.label ?? 'Tracked items'}
            value={kpiMap.get('total_skus')?.value ?? 0}
            subtitle="Unique inventory rows being tracked"
            icon={HiOutlineCube}
            accent="linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
            loading={showSkeleton}
          />
        </GridItem>

        <GridItem colSpan={{ base: 12, md: 6, xl: 3 }} minW={0}>
          <StatCard
            title={kpiMap.get('total_available_qty')?.label ?? 'Available qty'}
            value={kpiMap.get('total_available_qty')?.value ?? 0}
            subtitle={`Net available: ${formatNumber(
              kpiMap.get('total_available_qty')?.meta?.net_available_qty ?? 0,
            )}`}
            icon={HiOutlineArchiveBox}
            accent="linear-gradient(135deg, #14532d 0%, #166534 100%)"
            loading={showSkeleton}
          />
        </GridItem>

        <GridItem colSpan={{ base: 12, md: 6, xl: 3 }} minW={0}>
          <StatCard
            title={kpiMap.get('pending_qc_qty')?.label ?? 'Pending QC qty'}
            value={kpiMap.get('pending_qc_qty')?.value ?? 0}
            subtitle="Received quantity not fully dispositioned"
            icon={HiOutlineClipboardDocumentList}
            accent="linear-gradient(135deg, #581c87 0%, #7e22ce 100%)"
            loading={showSkeleton}
          />
        </GridItem>

        <GridItem colSpan={{ base: 12, md: 6, xl: 3 }} minW={0}>
          <StatCard
            title={kpiMap.get('open_shortages')?.label ?? 'Open shortages'}
            value={kpiMap.get('open_shortages')?.value ?? 0}
            subtitle={`Fabricated 30d: ${formatNumber(
              kpiMap.get('fabricated_output_30d')?.value ?? 0,
            )}`}
            icon={HiOutlineShieldExclamation}
            accent="linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)"
            loading={showSkeleton}
          />
        </GridItem>
      </Grid>

      <Grid templateColumns="repeat(12, minmax(0, 1fr))" gap={4} alignItems="stretch">
        <GridItem colSpan={{ base: 12, xl: 4 }} minW={0} display="flex">
          <Box w="full" minW={0}>
            <Card>
              <Box
                h={TOP_PANEL_HEIGHT}
                display="flex"
                flexDirection="column"
                minH={0}
                overflow="hidden"
              >
                <SectionHeader
                  title="Stock health"
                  subtitle="Distribution of tracked items by live inventory status"
                  icon={HiOutlineArrowTrendingUp}
                />

                <Skeleton loading={showSkeleton}>
                  <Flex
                    h="100%"
                    flexDirection="column"
                    justifyContent="space-between"
                    minH={0}
                  >
                    <Box h={STOCK_CHART_HEIGHT} minH={STOCK_CHART_HEIGHT} flexShrink={0}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={stockBreakdown}
                            dataKey="value"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            innerRadius={58}
                            outerRadius={86}
                            paddingAngle={3}
                          >
                            {stockBreakdown.map((entry) => (
                              <Cell
                                key={entry.key}
                                fill={STATUS_COLORS[entry.key] ?? '#94a3b8'}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>

                    <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3} mt={2}>
                      {stockBreakdown.map((item) => (
                        <Flex
                          key={item.key}
                          p={3}
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor={PANEL_BORDER}
                          bg={SUBTLE_BG}
                          justifyContent="space-between"
                          alignItems="center"
                          minW={0}
                        >
                          <HStack gap={2} minW={0}>
                            <Box
                              w="10px"
                              h="10px"
                              borderRadius="full"
                              bg={STATUS_COLORS[item.key] ?? 'gray.400'}
                              flexShrink={0}
                            />
                            <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                              {item.label}
                            </Text>
                          </HStack>
                          <Text fontWeight="bold" flexShrink={0}>
                            {item.value}
                          </Text>
                        </Flex>
                      ))}
                    </Grid>
                  </Flex>
                </Skeleton>
              </Box>
            </Card>
          </Box>
        </GridItem>

        <GridItem colSpan={{ base: 12, xl: 5 }} minW={0} display="flex">
          <Box w="full" minW={0}>
            <Card>
              <Box
                h={TOP_PANEL_HEIGHT}
                display="flex"
                flexDirection="column"
                minH={0}
                overflow="hidden"
              >
                <SectionHeader
                  title="Incoming receipts outlook"
                  subtitle="Overdue, due today, and upcoming expected receipts"
                  icon={HiOutlineChartBar}
                />

                <Skeleton loading={showSkeleton}>
                  <Box h="340px" minH="340px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={receiptsByDay}
                        margin={{ top: 12, right: 8, left: -18, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => formatShortDate(String(value))}
                          fontSize={12}
                        />
                        <YAxis allowDecimals={false} fontSize={12} />
                        <Tooltip
                          labelFormatter={(value) => formatLongDate(String(value))}
                        />
                        <Legend />
                        <Bar
                          dataKey="due_today"
                          name="Due today"
                          stackId="a"
                          fill="#f59e0b"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="overdue"
                          name="Overdue"
                          stackId="a"
                          fill="#ef4444"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="upcoming"
                          name="Upcoming"
                          stackId="a"
                          fill="#3b82f6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Skeleton>
              </Box>
            </Card>
          </Box>
        </GridItem>

        <GridItem colSpan={{ base: 12, xl: 3 }} minW={0} display="flex">
          <Box w="full" minW={0}>
            <Card>
              <Box
                h={SHORTAGE_PANEL_HEIGHT}
                display="flex"
                flexDirection="column"
                minH={0}
                overflow="hidden"
              >
                <SectionHeader
                  title="Top shortage parts"
                  subtitle="Most open shortage pressure"
                  icon={HiOutlineExclamationTriangle}
                />

                {showSkeleton ? (
                  <Skeleton h={SHORTAGE_SCROLL_HEIGHT} borderRadius="xl" />
                ) : (
                  <Box
                    h={SHORTAGE_SCROLL_HEIGHT}
                    minH={SHORTAGE_SCROLL_HEIGHT}
                    maxH={SHORTAGE_SCROLL_HEIGHT}
                    overflowY="auto"
                    overflowX="hidden"
                    pr={2}
                    sx={{
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                    }}
                  >
                    <Flex flexDirection="column" gapY={3}>
                      {topShortageParts.length === 0 ? (
                        <EmptyState text="No open shortages right now." />
                      ) : (
                        topShortageParts.slice(0, 10).map((item) => (
                          <Flex
                            key={item.part_id}
                            flexDirection="column"
                            gapY={2}
                            p={3}
                            borderRadius="xl"
                            borderWidth="1px"
                            borderColor={PANEL_BORDER}
                            bg={SUBTLE_BG}
                            flexShrink={0}
                            minW={0}
                          >
                            <Flex
                              justifyContent="space-between"
                              alignItems="flex-start"
                              gap={3}
                            >
                              <Box minW={0} flex="1">
                                <Text fontWeight="bold" noOfLines={1}>
                                  {item.part_number}
                                </Text>
                                <Text fontSize="sm" color="fg.muted" noOfLines={2}>
                                  {item.description || 'No description'}
                                </Text>
                              </Box>

                              <Badge
                                px={3}
                                py={1}
                                borderRadius="full"
                                bg="red.50"
                                color="red.700"
                                borderWidth="1px"
                                borderColor="red.100"
                                flexShrink={0}
                              >
                                {item.open_short_qty}
                              </Badge>
                            </Flex>

                            <HStack gap={2} flexWrap="wrap">
                              <Badge bg="bg.emphasized" color="fg">
                                {item.open_log_count} logs
                              </Badge>
                              {item.last_shortage_at ? (
                                <Badge bg="bg.emphasized" color="fg">
                                  {formatRelativeLite(item.last_shortage_at)}
                                </Badge>
                              ) : null}
                            </HStack>
                          </Flex>
                        ))
                      )}
                    </Flex>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </GridItem>
      </Grid>

      <Grid templateColumns="repeat(12, minmax(0, 1fr))" gap={4} alignItems="stretch">
        <GridItem colSpan={{ base: 12, xl: 8 }} minW={0} display="flex">
          <Box w="full" minW={0}>
            <Card>
              <Box
                h={DETAIL_PANEL_HEIGHT}
                display="flex"
                flexDirection="column"
                minH={0}
                overflow="hidden"
              >
                <Flex
                  justifyContent="space-between"
                  alignItems={{ base: 'stretch', xl: 'center' }}
                  flexDirection={{ base: 'column', xl: 'row' }}
                  gap={3}
                  mb={4}
                  flexShrink={0}
                >
                  <Box minW={0}>
                    <Text fontWeight="bold" fontSize="lg">
                      Inventory detail
                    </Text>
                    <Text fontSize="sm" color="fg.muted">
                      Live stock, incoming, QC, shortages, and fabrication movement
                    </Text>
                  </Box>

                  <HStack gap={3} flexWrap="wrap">
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search part number or description"
                      maxW="280px"
                      bg={SUBTLE_BG}
                      borderColor={PANEL_BORDER}
                    />

                    <Box
                      as="select"
                      value={statusFilter}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setStatusFilter(
                          e.target.value as
                            | 'ALL'
                            | InventoryDashboardInventoryRow['status'],
                        )
                      }
                      maxW="220px"
                      h="40px"
                      px={3}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={PANEL_BORDER}
                      bg={SUBTLE_BG}
                      color="fg"
                      outline="none"
                    >
                      <option value="ALL">All statuses</option>
                      <option value="IN_STOCK">In stock</option>
                      <option value="LOW_STOCK">Low stock</option>
                      <option value="OUT_OF_STOCK">Out of stock</option>
                      <option value="INCOMING_ONLY">Incoming only</option>
                    </Box>
                  </HStack>
                </Flex>

                <Skeleton loading={showSkeleton} style={{ height: '100%' }}>
                  <Box
                    borderWidth="1px"
                    borderColor={PANEL_BORDER}
                    borderRadius="xl"
                    flex="1"
                    minH={0}
                    h="100%"
                    overflow="auto"
                    sx={{
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                    }}
                  >
                    <Table.Root size="sm" native>
                      <thead>
                        <tr>
                          <th style={tableHeadStyleStickyLeft}>Part</th>
                          <th style={tableHeadStyleNumeric}>Available</th>
                          <th style={tableHeadStyleNumeric}>On hold</th>
                          <th style={tableHeadStyleNumeric}>Net</th>
                          <th style={tableHeadStyleNumeric}>Incoming</th>
                          <th style={tableHeadStyleNumeric}>Pending QC</th>
                          <th style={tableHeadStyleNumeric}>Consumed 30d</th>
                          <th style={tableHeadStyleNumeric}>Fabricated 30d</th>
                          <th style={tableHeadStyleNumeric}>Shortage</th>
                          <th style={tableHeadStyle}>Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredRows.length === 0 ? (
                          <tr>
                            <td style={{ padding: '20px' }} colSpan={10}>
                              <EmptyState
                                text="No rows match the current filters."
                                compact
                              />
                            </td>
                          </tr>
                        ) : (
                          filteredRows.map((row) => (
                            <tr key={row.row_key}>
                              <td style={tableCellPartStickyStyle}>
                                <Box>
                                  <Text fontWeight="bold">{row.part_number}</Text>
                                  <Text fontSize="xs" color="fg.muted" noOfLines={2}>
                                    {row.description || row.source_type}
                                  </Text>
                                </Box>
                              </td>
                              <td style={tableCellNumericStyle}>
                                {formatNumber(row.available_qty)}
                              </td>
                              <td style={tableCellNumericStyle}>
                                {formatNumber(row.on_hold_qty)}
                              </td>
                              <td style={tableCellNumericStyle}>
                                <Text as="span" fontWeight="bold">
                                  {formatNumber(row.net_available_qty)}
                                </Text>
                              </td>
                              <td style={tableCellNumericStyle}>
                                <Flex flexDirection="column" alignItems="flex-end">
                                  <Text>{formatNumber(row.incoming_open_qty)}</Text>
                                  {row.overdue_incoming_qty > 0 ? (
                                    <Text fontSize="xs" color="red.500">
                                      overdue {formatNumber(row.overdue_incoming_qty)}
                                    </Text>
                                  ) : null}
                                </Flex>
                              </td>
                              <td style={tableCellNumericStyle}>
                                {formatNumber(row.pending_qc_qty)}
                              </td>
                              <td style={tableCellNumericStyle}>
                                {formatNumber(row.consumed_30d_qty)}
                              </td>
                              <td style={tableCellNumericStyle}>
                                {formatNumber(row.fabricated_30d_qty)}
                              </td>
                              <td style={tableCellNumericStyle}>
                                <Text
                                  as="span"
                                  fontWeight="bold"
                                  color={
                                    row.open_shortage_qty > 0 ? 'red.500' : 'inherit'
                                  }
                                >
                                  {formatNumber(row.open_shortage_qty)}
                                </Text>
                              </td>
                              <td style={tableCellStyle}>
                                <Flex gap={1} alignItems="center">
                                  <StatusPill status={row.status} />
                                  {row.part_id ? (
                                    <TrackingActions
                                      compact
                                      size="xs"
                                      source={{
                                        source_type: 'part',
                                        source_id: row.part_id,
                                        label: row.part_number,
                                      }}
                                      context={{
                                        page: 'inventory',
                                        project_id: selectedProject?.id ?? null,
                                      }}
                                    />
                                  ) : null}
                                </Flex>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table.Root>
                  </Box>
                </Skeleton>
              </Box>
            </Card>
          </Box>
        </GridItem>

        <GridItem colSpan={{ base: 12, xl: 4 }} minW={0} display="flex">
          <Box w="full" minW={0}>
            <Card>
              <Box
                h={ACTIVITY_PANEL_HEIGHT}
                display="flex"
                flexDirection="column"
                minH={0}
                overflow="hidden"
              >
                <SectionHeader
                  title="Recent activity"
                  subtitle="Latest receipts, QC, consumption, and fabrication output"
                  icon={HiOutlineCog6Tooth}
                />

                {showSkeleton ? (
                  <Skeleton h={ACTIVITY_SCROLL_HEIGHT} borderRadius="xl" />
                ) : (
                  <Box
                    h={ACTIVITY_SCROLL_HEIGHT}
                    minH={ACTIVITY_SCROLL_HEIGHT}
                    maxH={ACTIVITY_SCROLL_HEIGHT}
                    overflowY="auto"
                    overflowX="hidden"
                    pr={2}
                    sx={{
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                    }}
                  >
                    <Flex flexDirection="column" gapY={3}>
                      {recentActivity.length === 0 ? (
                        <EmptyState text="No recent activity found." />
                      ) : (
                        recentActivity.slice(0, 18).map((item, index) => {
                          const badgeStyle = ACTIVITY_STYLES[item.type];

                          return (
                            <Flex
                              key={`${item.type}-${item.created_at}-${index}`}
                              flexDirection="column"
                              gapY={2}
                              p={3}
                              borderRadius="xl"
                              borderWidth="1px"
                              borderColor={PANEL_BORDER}
                              bg={SUBTLE_BG}
                              flexShrink={0}
                              minW={0}
                            >
                              <Flex
                                justifyContent="space-between"
                                alignItems="flex-start"
                                gap={3}
                              >
                                <Box minW={0} flex="1">
                                  <Text fontWeight="bold" noOfLines={2}>
                                    {item.title}
                                  </Text>
                                  {item.subtitle ? (
                                    <Text fontSize="sm" color="fg.muted" noOfLines={2}>
                                      {item.subtitle}
                                    </Text>
                                  ) : null}
                                </Box>

                                <Badge
                                  px={3}
                                  py={1}
                                  borderRadius="full"
                                  bg={badgeStyle.bg}
                                  color={badgeStyle.color}
                                  borderWidth="1px"
                                  borderColor="transparent"
                                  flexShrink={0}
                                >
                                  {badgeStyle.label}
                                </Badge>
                              </Flex>

                              <Flex
                                justifyContent="space-between"
                                alignItems="center"
                                gap={3}
                              >
                                <Text fontSize="xs" color="fg.muted">
                                  {formatDateTime(item.created_at)}
                                </Text>
                                {typeof item.quantity === 'number' ? (
                                  <Text fontWeight="bold" flexShrink={0}>
                                    {formatNumber(item.quantity)}
                                  </Text>
                                ) : null}
                              </Flex>
                            </Flex>
                          );
                        })
                      )}
                    </Flex>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
        </GridItem>
      </Grid>
    </Flex>
  );
};

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  accent,
  loading,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ElementType;
  accent: string;
  loading?: boolean;
}) => {
  return (
    <Card>
      <Skeleton loading={loading}>
        <Flex
          flexDirection="column"
          gapY={3}
          p={4}
          borderRadius="2xl"
          color="white"
          bg={accent}
          minH="132px"
        >
          <Flex justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Text fontSize="sm" opacity={0.85}>
                {title}
              </Text>
              <Text fontSize="3xl" fontWeight="bold" mt={2}>
                {formatNumber(Number(value) || 0)}
              </Text>
            </Box>

            <Flex
              w="38px"
              h="38px"
              borderRadius="xl"
              alignItems="center"
              justifyContent="center"
              bg="rgba(255,255,255,0.14)"
            >
              <Icon size={18} />
            </Flex>
          </Flex>

          <Text fontSize="sm" opacity={0.88}>
            {subtitle}
          </Text>
        </Flex>
      </Skeleton>
    </Card>
  );
};

const SectionHeader = ({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ElementType;
}) => {
  return (
    <Flex justifyContent="space-between" alignItems="flex-start" mb={3} gap={3}>
      <Box minW={0}>
        <Text fontWeight="bold" fontSize="lg">
          {title}
        </Text>
        {subtitle ? (
          <Text fontSize="sm" color="fg.muted" mt={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>

      {Icon ? (
        <Flex
          w="40px"
          h="40px"
          borderRadius="xl"
          bg={SUBTLE_BG}
          borderWidth="1px"
          borderColor={PANEL_BORDER}
          alignItems="center"
          justifyContent="center"
          color="fg.muted"
          flexShrink={0}
        >
          <Icon size={18} />
        </Flex>
      ) : null}
    </Flex>
  );
};

const StatusPill = ({
  status,
}: {
  status: InventoryDashboardInventoryRow['status'];
}) => {
  const style = STATUS_STYLES[status];

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      bg={style.bg}
      color={style.color}
      borderWidth="1px"
      borderColor="transparent"
      textTransform="none"
    >
      {style.label}
    </Badge>
  );
};

const EmptyState = ({
  text,
  compact = false,
}: {
  text: string;
  compact?: boolean;
}) => {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      minH={compact ? '72px' : '180px'}
      borderWidth="1px"
      borderStyle="dashed"
      borderColor={PANEL_BORDER}
      borderRadius="xl"
      bg={SUBTLE_BG}
      p={4}
    >
      <Text color="fg.muted" textAlign="center">
        {text}
      </Text>
    </Flex>
  );
};

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value || 0);
}

function formatShortDate(value?: string) {
  if (!value) return '—';
  return dayjs(value).format('MMM D');
}

function formatLongDate(value?: string) {
  if (!value) return '—';
  return dayjs(value).format('MMM D, YYYY');
}

function formatDateTime(value?: string) {
  if (!value) return '—';
  return dayjs(value).format('MMM D, h:mm A');
}

function formatRelativeLite(value?: string) {
  if (!value) return '—';
  return dayjs(value).format('MMM D');
}

const tableHeadStyle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: 'var(--chakra-colors-fg-muted)',
  padding: '12px 16px',
  background: 'var(--chakra-colors-bg-muted)',
  borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const tableHeadStyleStickyLeft: React.CSSProperties = {
  ...tableHeadStyle,
  left: 0,
  zIndex: 3,
  background: 'var(--chakra-colors-bg-muted)',
};

const tableHeadStyleNumeric: React.CSSProperties = {
  ...tableHeadStyle,
  textAlign: 'right',
};

const tableCellStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
  verticalAlign: 'top',
};

const tableCellPartStickyStyle: React.CSSProperties = {
  ...tableCellStyle,
  minWidth: '220px',
  position: 'sticky',
  left: 0,
  zIndex: 1,
  background: 'var(--chakra-colors-bg)',
  color: 'var(--chakra-colors-fg)',
  boxShadow: '1px 0 0 rgba(148, 163, 184, 0.12)',
};

const tableCellNumericStyle: React.CSSProperties = {
  ...tableCellStyle,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

export default InventoryDashboard;